import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Caas from '../../../../../api/caas';
import useApiQuery from '../../../../../hooks/useApiQuery';
import usePolling from '../../../../../hooks/usePolling';

import { subscribeToClusterUpgradeStream } from './clusterUpgradeStream';

import type { ClusterStatusResponse, ClusterUpgradeStatusItem } from '@/types/caasTypes';

const POLL_INTERVAL_MS = 5000;

interface UseClusterUpgradeStatusOptions {
  enabled?: boolean;
  upgradeRequested?: boolean;
}

interface UseClusterUpgradeStatusReturn {
  statusItem: ClusterUpgradeStatusItem | undefined;
  latestStatus: ClusterStatusResponse | undefined;
  statusError: Error | null;
  isLive: boolean;
  connectionLost: boolean;
  isUpgradeInProgress: boolean;
  isUpgradeFinished: boolean;
  isPostCheckResolved: boolean;
  isAwaitingPostCheck: boolean;
  canRunValidationCheck: boolean;
  refetchStatus: () => void;
}

const isPostCheckValueResolved = (postCheck: string | undefined): boolean =>
  postCheck === 'passed' ||
  postCheck === 'success' ||
  postCheck === 'failed' ||
  postCheck === 'error';

/**
 * Live cluster-upgrade status. SSE is the primary transport - it replaces the
 * old always-on poll loop. Polling only runs as a fallback once the stream has
 * exhausted its reconnect attempts, reusing the exact same interval/stop logic
 * that drove the feature before SSE existed.
 */
export default function useClusterUpgradeStatus(
  clusterName: string | undefined,
  options: UseClusterUpgradeStatusOptions = {}
): UseClusterUpgradeStatusReturn {
  const { enabled = true, upgradeRequested = false } = options;
  const upgradeRequestedRef = useRef(upgradeRequested);
  useEffect(() => {
    upgradeRequestedRef.current = upgradeRequested;
  }, [upgradeRequested]);

  const [streamStatusItem, setStreamStatusItem] = useState<ClusterUpgradeStatusItem | undefined>(
    undefined
  );
  const [useFallbackPolling, setUseFallbackPolling] = useState(false);

  const {
    makeApiCall: fetchClusterStatus,
    data: clusterStatus,
    error: statusError,
  } = useApiQuery<ClusterStatusResponse>(
    Caas.fetchClusterStatus as unknown as (args: unknown) => Promise<ClusterStatusResponse>,
    { disableLoader: true }
  );

  const { makeApiCall: pollClusterStatus, data: polledStatus } = useApiQuery<ClusterStatusResponse>(
    Caas.fetchClusterStatus as unknown as (args: unknown) => Promise<ClusterStatusResponse>,
    { disableLoader: true }
  );

  const latestStatus = polledStatus ?? clusterStatus;
  const restStatusItem = latestStatus?.items?.[0];
  // Once fallback polling is active, the polled snapshot always wins - even
  // if streamStatusItem somehow still held a stale SSE value, it must not
  // resurface and mask a fresher polled result.
  const statusItem = useFallbackPolling ? restStatusItem : (streamStatusItem ?? restStatusItem);
  const statusInfo = statusItem?.status;

  const isUpgradeInProgress = statusInfo?.finished === false;
  const isUpgradeFinished = statusInfo?.finished === true;
  const isPostCheckResolved = isPostCheckValueResolved(statusInfo?.postCheck);
  const isAwaitingPostCheck = isUpgradeFinished && !isPostCheckResolved;
  const canRunValidationCheck = !isUpgradeInProgress && !isAwaitingPostCheck;

  const pollingEnabled =
    enabled && (isUpgradeInProgress || isAwaitingPostCheck || upgradeRequested);

  const refetchInterval = useCallback((data: ClusterStatusResponse | undefined): number | false => {
    const item = data?.items?.[0];
    const st = item?.status;
    if (st?.finished === true) {
      return isPostCheckValueResolved(st.postCheck) ? false : POLL_INTERVAL_MS;
    }
    if (st?.finished === false) return POLL_INTERVAL_MS;
    if (upgradeRequestedRef.current) return POLL_INTERVAL_MS;
    return false;
  }, []);

  const pollingArgs = useMemo(() => (clusterName ? [clusterName] : undefined), [clusterName]);

  const {
    isPolling: fallbackIsPolling,
    pollingError: fallbackPollingError,
    refetch: fallbackRefetch,
  } = usePolling<ClusterStatusResponse>(pollClusterStatus, latestStatus ?? undefined, {
    enabled: pollingEnabled && useFallbackPolling,
    ...(pollingArgs ? { args: pollingArgs } : {}),
    refetchInterval,
  });

  useEffect(() => {
    if (!enabled || !clusterName) return;
    fetchClusterStatus(clusterName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterName, enabled]);

  useEffect(() => {
    setStreamStatusItem(undefined);
    setUseFallbackPolling(false);
  }, [clusterName]);

  useEffect(() => {
    if (!pollingEnabled) {
      setUseFallbackPolling(false);
    }
  }, [pollingEnabled]);

  useEffect(() => {
    if (!pollingEnabled || !clusterName || useFallbackPolling) return undefined;

    return subscribeToClusterUpgradeStream(clusterName, {
      onStatus: item => {
        setStreamStatusItem(item);
      },
      onUnavailable: () => {
        // Otherwise `streamStatusItem ?? restStatusItem` keeps returning this
        // frozen last-SSE-value forever once fallback polling takes over -
        // every fresh polled result would be silently ignored until
        // `clusterName` changes.
        setStreamStatusItem(undefined);
        setUseFallbackPolling(true);
      },
    });
  }, [pollingEnabled, clusterName, useFallbackPolling]);

  const refetchStatus = useCallback(() => {
    if (useFallbackPolling) {
      fallbackRefetch();
      return;
    }
    if (clusterName) {
      pollClusterStatus(clusterName);
    }
  }, [useFallbackPolling, fallbackRefetch, clusterName, pollClusterStatus]);

  return {
    statusItem,
    latestStatus,
    statusError,
    isLive: useFallbackPolling ? fallbackIsPolling : pollingEnabled,
    connectionLost: useFallbackPolling ? fallbackPollingError : false,
    isUpgradeInProgress,
    isUpgradeFinished,
    isPostCheckResolved,
    isAwaitingPostCheck,
    canRunValidationCheck,
    refetchStatus,
  };
}
